import React, { useEffect, useRef, useState } from "react";
import { Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import { SOCIALS } from "../data/content";
import "./community-bar.css";

const API = `${window.__BACKEND__}/api`;
const FALLBACK_TOTAL = 80600;

function ThreadsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 192 192" fill="currentColor" aria-hidden="true">
      <path d="M141.537 88.988c-.827-.396-1.667-.778-2.518-1.143-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.694 14.724-10.548 21.347-10.548h.23c8.25.053 14.475 2.451 18.504 7.129 2.932 3.405 4.893 8.111 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.141-23.82 1.372-39.134 15.265-38.105 34.569.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.809-.169-40.06-7.484-51.275-21.742C29.236 139.966 23.808 120.682 23.605 96c.203-24.682 5.631-43.966 16.134-57.317C50.954 24.425 68.204 17.11 91.013 16.94c22.975.171 40.526 7.521 52.171 21.848 5.71 7.026 10.015 15.861 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C141.036 9.607 119.202.195 91.07 0h-.113C62.882.194 41.292 9.642 26.788 28.079 13.882 44.486 7.224 67.316 7.001 95.933L7 96l.001.067c.223 28.617 6.881 51.447 19.787 67.854C41.292 182.358 62.882 191.806 90.957 192h.113c24.96-.173 42.554-6.708 57.048-21.192 18.963-18.943 18.392-42.693 12.142-57.266-4.484-10.455-13.033-18.946-24.723-24.554zM98.44 129.507c-10.44.588-21.286-4.098-21.82-14.135-.397-7.442 5.296-15.746 22.461-16.735 1.966-.114 3.895-.169 5.79-.169 6.235 0 12.068.606 17.371 1.765-1.978 24.702-13.58 28.713-23.802 29.274z" />
    </svg>
  );
}
function TikTokIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
    </svg>
  );
}

const ICONS = {
  instagram: (p) => <Instagram size={18} {...p} />,
  facebook: (p) => <Facebook size={18} {...p} />,
  threads: () => <ThreadsIcon />,
  tiktok: () => <TikTokIcon />,
  linkedin: (p) => <Linkedin size={18} {...p} />,
  youtube: (p) => <Youtube size={18} {...p} />,
};
const COLORS = {
  instagram: "#E1306C",
  facebook: "#1877F2",
  threads: "#111318",
  tiktok: "#111318",
  linkedin: "#0A66C2",
  youtube: "#FF0000",
};
// Display order per mockup: IG, FB, Threads, TikTok, LinkedIn, YouTube
const ORDER = ["instagram", "facebook", "threads", "tiktok", "linkedin", "youtube"];
const LABELS = {
  instagram: "Instagram", facebook: "Facebook", threads: "Threads",
  tiktok: "TikTok", linkedin: "LinkedIn", youtube: "YouTube",
};

function fmt(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1000) {
    const k = n / 1000;
    const s = k >= 100 ? String(Math.round(k)) : k.toFixed(1).replace(".0", "").replace(".", ",");
    return s + "K";
  }
  return String(n);
}

export default function CommunityBar() {
  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(FALLBACK_TOTAL);
  const [display, setDisplay] = useState(0);
  const [visible, setVisible] = useState(false);
  const totalRef = useRef(FALLBACK_TOTAL);
  const barRef = useRef(null);
  const started = useRef(false);
  const done = useRef(false);
  totalRef.current = total;

  useEffect(() => {
    let alive = true;
    fetch(`${API}/social-followers`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive || !d) return;
        setCounts(d);
        const t = Object.values(d).reduce(
          (a, v) => (typeof v === "number" ? a + v : a),
          0
        );
        if (t > 0) setTotal(t);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Count-up when the bar enters the viewport (once). Reads totalRef each frame
  // so it always eases toward the latest fetched total (no stale fallback).
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const animate = () => {
      if (started.current) return;
      started.current = true;
      if (reduce) { done.current = true; setDisplay(totalRef.current); return; }
      const start = performance.now();
      const dur = 1600;
      const tick = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(Math.round(totalRef.current * eased));
        if (p < 1) requestAnimationFrame(tick);
        else { done.current = true; setDisplay(totalRef.current); }
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { io.disconnect(); setVisible(true); animate(); } },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // If the real total arrives after the count-up finished, snap to it.
  useEffect(() => {
    if (done.current) setDisplay(total);
  }, [total]);

  const byIcon = {};
  SOCIALS.forEach((s) => { byIcon[s.icon] = s; });

  return (
    <div
      ref={barRef}
      className={`font-figtree bg-white border-b border-[var(--vdh-border)] ${visible ? "vdh-cb--in" : ""}`}
      data-testid="community-bar"
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-[52px] flex items-center gap-3 md:gap-5">
        <div className="flex items-center gap-2 mr-auto shrink-0">
          <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--vdh-green)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" />
          </svg>
          <strong
            aria-label={`Oltre ${total.toLocaleString("it-IT")} followers`}
            className="font-bricolage tabular-nums-fix text-[#050505] text-[22px] md:text-[30px] leading-none tracking-[-0.02em] inline-flex items-center"
            style={{ minWidth: 118 }}
            data-testid="community-total"
          >
            {display.toLocaleString("it-IT")}
            <span className="text-[var(--vdh-green)]" aria-hidden="true">+</span>
          </strong>
          <span className="hidden sm:inline-flex items-center gap-1.5 self-center">
            <span className="vdh-cb-live" aria-hidden="true"><span className="ring" /><span /></span>
            <span className="text-[13px] font-bold text-[var(--vdh-text-2)]">followers</span>
          </span>
        </div>

        <div className="vdh-cb-scroll flex items-center gap-2 md:gap-2.5 overflow-x-auto no-scrollbar py-1 -my-1">
          {ORDER.map((icon, i) => {
            const s = byIcon[icon];
            const Icon = ICONS[icon];
            const c = COLORS[icon];
            const val = counts && typeof counts[icon] === "number" ? fmt(counts[icon]) : null;
            return (
              <a
                key={icon}
                href={s?.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${LABELS[icon]}: ${val || ""} follower`}
                data-testid={`community-social-${icon}`}
                className="vdh-cb-chip shrink-0"
                style={{
                  "--chip-c": c,
                  "--chip-bg": `${c}12`,
                  "--chip-bd": `${c}26`,
                  "--chip-bg-h": `${c}1f`,
                  "--chip-bd-h": `${c}4d`,
                  "--chip-sh": `${c}66`,
                  animationDelay: `${i * 60}ms`,
                }}
              >
                <span className="vdh-cb-chip__icon"><Icon /></span>
                {val && <strong className="vdh-cb-chip__val tabular-nums-fix">{val}</strong>}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
